
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import ManageSelectors from "./ManageSelectors.vue";
import EditSelector from "./EditSelector.vue";
import { globalStore, uiStore } from "@/store/store";
import { closeUiPanel } from "@/lib/panels/panels";
import { ClosingConfirmationChosenOption } from "@/types/ui";
import {
  refetchSelectorsFromServerToGlobalStore,
  saveSelectorsToWordPress
} from "@/lib/selectors/selectors";

@Component({
  components: {
    EditSelector,
    ManageSelectors
  }
})
export default class SelectorsPanel extends Vue {
  isLoadingData = false;
  isStoringData = false;

  get activeSelector() {
    return uiStore.activeSelector;
  }

  async beforeMount() {
    if (uiStore.panelState.shouldLoadFreshDataIntoStoreOnceOpened.selectors) {
      uiStore.setPanelState({
        ...uiStore.panelState,
        selectorsPanelPanelActiveViewMode: "controls"
      });
      await this.load();
    } else {
      uiStore.setShouldPanelLoadFreshDataIntoStoreOnceOpened("selectors", true);
    }
  }

  async load() {
    this.isLoadingData = true;
    const isSuccessful = await refetchSelectorsFromServerToGlobalStore();
    this.isLoadingData = false;

    return isSuccessful;
  }

  async save() {
    this.isStoringData = true;
    const isSuccessful = await saveSelectorsToWordPress();
    this.isStoringData = false;

    return isSuccessful;
  }

  requestPanelClosingConfirmation() {
    uiStore.requestClosingConfirmation({
      subject: "selectors",
      confirmationDefinition: {
        title: "You have unsaved changes to your Selectors.",
        text: "Would you like to save or discard these changes?",
        type: "info"
      }
    });
  }

  get unsavedChangesPresent(): boolean {
    return globalStore.selectorsHasUnsavedChangesPresent;
  }

  @Watch("unsavedChangesPresent", { immediate: true })
  handleUnsavedChangesPresentChange(unsavedChangesPresent: boolean) {
    if (unsavedChangesPresent) {
      this.requestPanelClosingConfirmation();
    } else {
      uiStore.noClosingConfirmationIsNeededFor("selectors");
    }
  }

  get confirmationChosenOption(): ClosingConfirmationChosenOption | undefined {
    return uiStore.closingConfirmations.selectors?.chosenOption;
  }

  @Watch("confirmationChosenOption", { immediate: true })
  async handleConfirmationChosenOptionChange(
    chosenOption: ClosingConfirmationChosenOption | undefined
  ) {
    if (chosenOption === "discard") {
      uiStore.setClosingConfirmationIsDiscarding({
        subject: "globalSettings",
        isDiscarding: true
      });
      if (await this.load()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    } else if (chosenOption === "save") {
      uiStore.setClosingConfirmationIsSaving({
        subject: "globalSettings",
        isSaving: true
      });
      if (await this.save()) {
        this.close();
      } else {
        this.requestPanelClosingConfirmation();
      }
    }
  }

  get isLoading() {
    return this.isLoadingData || this.isStoringData;
  }

  close() {
    closeUiPanel("selectors");
  }
}
